import { actionCounts } from '../utils/actionCounts';
import WorkflowCard, { WorkflowBreadcrumb } from '../components/WorkflowCard';
import { priorityLabel } from '../utils/presentation';
import { isTaskOverdue } from '../utils/taskPresentation';
import { matchesDueFilter } from '../utils/taskPresentation';
import RiskBadge from '../components/RiskBadge';
import { getRiskLevel, RiskLevel, riskLabel } from '../utils/riskUtils';
import usePageTitle from '../hooks/usePageTitle';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Inbox,
  Calendar as CalendarIcon,
  List,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Search,
  Plus,
  Building2,
  Package
} from 'lucide-react';
import { TaskDetailResponse, User as ApiUser } from '../types';
import { tasksService, usersService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ActionDetailModal from '../components/Forms/ActionDetailModal';
import AddTaskModal from '../components/Forms/AddTaskModal';
import { formatMonthYear, formatShortDate } from '../utils/dateUtils';
import './ActionsInbox.css';

type ViewMode = 'list' | 'calendar';
type StatusFilter = 'all' | 'not_started' | 'acknowledged' | 'started' | 'blocked' | 'complete' | 'overdue';
type PriorityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low';

const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };



const ActionsInbox: React.FC = () => {
  const { currentUser } = useAuth();
  usePageTitle('Actions');
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [tasks, setTasks] = useState<TaskDetailResponse[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(() => new URLSearchParams(location.search).get('view') === 'calendar' ? 'calendar' : 'list');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [ratingFilter, setRatingFilter] = useState<RiskLevel | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [showCancelled, setShowCancelled] = useState(false);
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [responsiblePersonFilter, setResponsiblePersonFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('all'); // all, today, week, overdue
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [hoveredTask, setHoveredTask] = useState<{ task: TaskDetailResponse; rect: DOMRect } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Deep-link: open task detail modal when ?task=<id> is present in the URL
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    if (taskId) {
      setSelectedTaskId(taskId);
      setIsModalOpen(true);
      // Remove the query param so refreshing doesn't re-open the modal
      params.delete('task');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [loading, location.search, navigate, setSelectedTaskId, setIsModalOpen]);

  const counts = actionCounts(tasks);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      void usersService.getUsers().then(setUsers).catch(() => {});
      const tasksData = await tasksService.getInbox(100, true, {
        all_items: true,
        ...(responsiblePersonFilter !== "all" ? { responsible_user_id: responsiblePersonFilter } : {}),
      });

      setTasks(tasksData);
    } catch (err) {
      console.error('Error loading actions:', err);
    } finally {
      setLoading(false);
    }
  }, [responsiblePersonFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleViewDetails = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTaskId(null);
  };

  const handleMarkComplete = async (taskId: string) => {
    try {
      await tasksService.completeTask(taskId);
      // Reload data to reflect the updated task status
      await loadData();
    } catch (err) {
      console.error('Error completing task:', err);
      showToast({
        type: 'error',
        title: 'Could not complete action',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const getStatusBadge = (task: TaskDetailResponse) => {
    const isOverdue = isTaskOverdue(task);

    if (isOverdue) {
      return { class: 'status-badge overdue', label: 'Overdue', icon: AlertTriangle };
    }

    if (task.due_by && new Date(task.due_by).toDateString() === new Date().toDateString() && !['complete', 'cancelled'].includes(task.status)) return {class: 'status-badge due-today', label: 'Due today', icon: Clock};
    switch (task.status) {
      case 'not_started':
        return { class: 'status-badge not-started', label: 'Not Started', icon: Clock };
      case 'acknowledged':
        return { class: 'status-badge acknowledged', label: 'Acknowledged', icon: CheckCircle };
      case 'started':
        return { class: 'status-badge started', label: 'In Progress', icon: Clock };
      case 'blocked':
        return { class: 'status-badge blocked', label: 'Blocked', icon: AlertTriangle };
      case 'complete':
        return { class: 'status-badge complete', label: 'Complete', icon: CheckCircle };
      default:
        return { class: 'status-badge', label: task.status, icon: Clock };
    }
  };

  const getPriorityClass = (task: TaskDetailResponse) => {
    const level = getTaskPriority(task);
    return `priority-${level}`;
  };

  const getTaskPriority = (task: TaskDetailResponse): string => task.priority || 'medium';

  const clientOptions = useMemo(
    () =>
      Array.from(
        new Map(
          tasks
            .filter((task) => task.client_id && task.client_name)
            .map((task) => [task.client_id as string, task.client_name as string]),
        ).entries(),
      ).map(([id, name]) => ({ id, name })),
    [tasks],
  );

  const filterTasks = () => {
    return tasks.filter(task => {
      // Hide cancelled tasks unless explicitly shown
      if (!showCancelled && task.status === 'cancelled') return false;

      // Search filter
      const normalizedSearch = searchTerm.toLowerCase();
      if (
        searchTerm &&
        ![
          task.description,
          task.client_name,
          task.facility_name,
          task.asset_name,
        ]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(normalizedSearch))
      ) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'overdue') {
          const isOverdue = isTaskOverdue(task);
          if (!isOverdue) return false;
        } else if (task.status !== statusFilter) {
          return false;
        }
      }

      if (ratingFilter !== 'all' && getRiskLevel(task.risk_likelihood ?? 0, task.risk_consequence ?? 0) !== ratingFilter) return false;
      // Priority filter
      if (priorityFilter !== 'all') {
        if (getTaskPriority(task) !== priorityFilter) return false;
      }

      // Client filter
      if (clientFilter !== 'all' && task.client_id !== clientFilter) {
        return false;
      }

      // Responsible person filter
      if (responsiblePersonFilter !== 'all' && task.responsible_user_id !== responsiblePersonFilter) {
        return false;
      }

      // Date filter
      if (!matchesDueFilter(task, dateFilter)) return false;

      return true;
    }).sort((a, b) => {
      // Sort by priority: critical → high → medium → low
      const aRisk = PRIORITY_ORDER[getTaskPriority(a)] ?? 2;
      const bRisk = PRIORITY_ORDER[getTaskPriority(b)] ?? 2;
      if (aRisk !== bRisk) return aRisk - bRisk;
      // Within the same priority, overdue items first
      const aOverdue = isTaskOverdue(a) ? 0 : 1;
      const bOverdue = isTaskOverdue(b) ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      // Then by due date ascending
      if (a.due_by && b.due_by) return new Date(a.due_by).getTime() - new Date(b.due_by).getTime();
      return 0;
    });
  };

  const filteredTasks = filterTasks();

  const renderListView = () => {
    if (filteredTasks.length === 0) {
      return (
        <div className="empty-state">
          <Inbox size={48} />
          <h3>No Actions Found</h3>
          <p>
            {tasks.length === 0
              ? 'No action items have been created yet.'
              : 'No actions match your current filters.'}
          </p>
        </div>
      );
    }

    return (
      <div className="tasks-list">
        {filteredTasks.map(task => {
          if (task.workflow) return <WorkflowCard canMarkRetest={!!currentUser && ['consultant', 'h2okay_admin'].includes(currentUser.role)} onRetestMarked={loadData} key={task.id} task={task} currentUserId={currentUser?.id} onOpen={handleViewDetails} onComplete={async id => { await tasksService.completeTask(id); await loadData(); }} />;
          const badge = getStatusBadge(task);
          const BadgeIcon = badge.icon;
          const priorityClass = getPriorityClass(task);
          const riskLevel = getTaskPriority(task);

          const assigneeName = users.find(u => u.id === task.responsible_user_id)?.name;

          return (
            <div
              key={task.id}
              className={`task-card ${priorityClass}`}
              onClick={() => handleViewDetails(task.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleViewDetails(task.id)}
            >
              <WorkflowBreadcrumb task={task} onOpen={handleViewDetails} /><h4 className="task-description">{task.description}</h4>
              <div className="task-meta-row">
                <span className={badge.class}>
                  <BadgeIcon size={12} />
                  {badge.label}
                </span>
                {task.risk_likelihood != null && task.risk_consequence != null && <RiskBadge likelihood={task.risk_likelihood} consequence={task.risk_consequence} />}
                <span title="Priority" className={`risk-badge risk-${riskLevel}`}>
                  {riskLevel === 'critical' && <AlertTriangle size={11} />}
                  {priorityLabel(riskLevel)}
                </span>
                {task.due_by && (
                  <span className="task-meta-chip">
                    <CalendarIcon size={12} />
                    {formatShortDate(task.due_by)}
                  </span>
                )}
                {task.client_name && (
                  <span className="task-meta-chip">
                    <Building2 size={12} />
                    {task.client_name}
                  </span>
                )}
                {task.facility_name && (
                  <span className="task-meta-chip">{task.facility_name}</span>
                )}
                {task.asset_name && (
                  <span className="task-meta-chip">
                    <Package size={12} />
                    {task.asset_name}
                  </span>
                )}
                {assigneeName && (
                  <span className="task-meta-chip task-assignee-chip">
                    <User size={12} />
                    {assigneeName}
                  </span>
                )}
                {task.status !== 'complete' && (
                  <button
                    className="task-complete-btn"
                    title="Mark complete"
                    onClick={(e) => { e.stopPropagation(); handleMarkComplete(task.id); }}
                  >
                    <CheckCircle size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };


  const renderCalendarView = () => {
    const now = new Date();
    const currentYear = calendarMonth.getFullYear();
    const currentMonth = calendarMonth.getMonth();

    // Get first day of month and number of days
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    // Generate calendar days
    const calendarDays: (number | null)[] = [];
    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }

    // Group tasks by date
    const tasksByDate = new Map<string, TaskDetailResponse[]>();
    filteredTasks.forEach(task => {
      if (task.due_by) {
        const dueDate = new Date(task.due_by);
        const dateKey = `${dueDate.getFullYear()}-${dueDate.getMonth()}-${dueDate.getDate()}`;
        const existing = tasksByDate.get(dateKey) || [];
        existing.push(task);
        tasksByDate.set(dateKey, existing);
      }
    });

    return (
      <div className="calendar-view">
        <div className="calendar-header">
          <h3 aria-live="polite">{formatMonthYear(firstDay)}</h3>
          <div className="calendar-controls" role="group" aria-label="Calendar navigation">
            <button className="btn btn-secondary btn-sm" onClick={() => setCalendarMonth(new Date(currentYear, currentMonth - 1, 1))}>Previous month</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setCalendarMonth(new Date())}>Today</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setCalendarMonth(new Date(currentYear, currentMonth + 1, 1))}>Next month</button>
          </div>
          <div className="calendar-legend">
            <span className="legend-item">
              <span className="legend-dot legend-critical"></span>{priorityLabel('critical')}
            </span>
            <span className="legend-item">
              <span className="legend-dot legend-high"></span>High
            </span>
            <span className="legend-item">
              <span className="legend-dot legend-medium"></span>Medium
            </span>
            <span className="legend-item">
              <span className="legend-dot legend-low"></span>Low
            </span>
          </div>
        </div>
        <div className="calendar-grid">
          <div className="calendar-day-header">Sun</div>
          <div className="calendar-day-header">Mon</div>
          <div className="calendar-day-header">Tue</div>
          <div className="calendar-day-header">Wed</div>
          <div className="calendar-day-header">Thu</div>
          <div className="calendar-day-header">Fri</div>
          <div className="calendar-day-header">Sat</div>

          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="calendar-day empty"></div>;
            }

            const dateKey = `${currentYear}-${currentMonth}-${day}`;
            const tasksForDay = tasksByDate.get(dateKey) || [];
            const isToday = day === now.getDate() && currentMonth === now.getMonth() && currentYear === now.getFullYear();

            return (
              <div key={day} className={`calendar-day ${isToday ? 'today' : ''}`}>
                <div className="day-number">{day}</div>
                <div className="day-tasks">
                  {tasksForDay.map(task => {
                    const user = users.find(u => u.id === task.responsible_user_id);
                    const badge = getStatusBadge(task);
                    const priorityClass = getPriorityClass(task);
                    const riskLevel = getTaskPriority(task);

                    return (
                      <div
                        key={task.id}
                        className={`calendar-task ${priorityClass}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleViewDetails(task.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleViewDetails(task.id); }}
                        onMouseEnter={(e) => {
                          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                          setHoveredTask({ task, rect: (e.currentTarget as HTMLElement).getBoundingClientRect() });
                        }}
                        onMouseLeave={() => {
                          hoverTimeoutRef.current = setTimeout(() => setHoveredTask(null), 120);
                        }}
                      >
                        <span className="task-dot"></span>
                        <span className="task-text">{task.description.substring(0, 30)}{task.description.length > 30 ? '...' : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-state">Loading actions...</div>
      </div>
    );
  }

  return (
    <div data-tour="actions" className="page-container actions-inbox">
      <div className="page-header">
        <div className="header-content">
          <h1>Actions Inbox</h1>
          <p>Track and manage action items from inspections and reviews</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setShowAddTaskModal(true);
          }}
        >
          <Plus size={18} />
          New Action
        </button>
      </div>

      <div className="actions-controls">
        <div className="controls-left">
          <div className="search-container">
            <Search size={18} />
            <input
              type="text"
              aria-label="Search actions"
              placeholder="Search actions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filters-group">
            <select
              aria-label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="not_started">Not Started</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="started">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="complete">Complete</option>
              <option value="overdue">Overdue</option>
            </select>

            <select className="filter-select" aria-label="Risk level" value={ratingFilter} onChange={e => setRatingFilter(e.target.value as RiskLevel | 'all')}>
              <option value="all">All risk levels</option>
              {(['low', 'moderate', 'high', 'very_high'] as RiskLevel[]).map(level => <option key={level} value={level}>{riskLabel(level)}</option>)}
            </select>
            <select
              aria-label="Priority"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
              className="filter-select"
            >
              <option value="all">All priorities</option>
              <option value="critical">{priorityLabel('critical')}</option>
              <option value="high">{priorityLabel('high')}</option>
              <option value="medium">{priorityLabel('medium')}</option>
              <option value="low">{priorityLabel('low')}</option>
            </select>

            <select
              aria-label="Client"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Clients</option>
              {clientOptions.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>

            <select
              aria-label="Assignee"
              value={responsiblePersonFilter}
              onChange={(e) => setResponsiblePersonFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Assignees</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>

            {viewMode === 'list' && (
              <select
                aria-label="Due date"
              value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Dates</option>
                <option value="today">Due Today</option>
                <option value="week">Due This Week</option>
                <option value="overdue">Overdue</option>
              </select>
            )}

            <button
              type="button"
              className={`filter-toggle-btn ${showCancelled ? 'active' : ''}`}
              onClick={() => setShowCancelled((v) => !v)}
              title="Show or hide cancelled tasks"
            >
              {showCancelled ? 'Hide Cancelled' : 'Show Cancelled'}
            </button>
          </div>
        </div>

        <div className="controls-right">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
              List
            </button>
            <button
              className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
            >
              <CalendarIcon size={16} />
              Calendar
            </button>
          </div>
        </div>
      </div>

      <div className="actions-stats">
        <button
          type="button"
          className={`stat-card ${statusFilter === 'not_started' ? 'stat-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'not_started' ? 'all' : 'not_started')}
        >
          <div className="stat-value">{tasks.filter(t => t.status === 'not_started').length}</div>
          <div className="stat-label">Not Started</div>
        </button>
        <button
          type="button"
          className={`stat-card ${statusFilter === 'started' ? 'stat-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'started' ? 'all' : 'started')}
        >
          <div className="stat-value">{tasks.filter(t => t.status === 'started').length}</div>
          <div className="stat-label">In Progress</div>
        </button>
        <button
          type="button"
          className={`stat-card ${statusFilter === 'complete' ? 'stat-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'complete' ? 'all' : 'complete')}
        >
          <div className="stat-value">{tasks.filter(t => t.status === 'complete').length}</div>
          <div className="stat-label">Complete</div>
        </button>
        <button
          type="button"
          className={`stat-card stat-overdue ${statusFilter === 'overdue' ? 'stat-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'overdue' ? 'all' : 'overdue')}
        >
          <div className="stat-value">
            {counts.overdue}
          </div>
          <div className="stat-label">Overdue</div>
        </button>
        <button
          type="button"
          className={`stat-card stat-critical ${priorityFilter === 'critical' ? 'stat-card--active' : ''}`}
          onClick={() => setPriorityFilter(priorityFilter === 'critical' ? 'all' : 'critical')}
        >
          <div className="stat-value">
            {counts.urgent}
          </div>
          <div className="stat-label">{priorityLabel('critical')} priority</div>
        </button>
      </div>

      <div className="actions-content">
        {viewMode === 'list' ? renderListView() : renderCalendarView()}
      </div>

      {/* Action Detail Modal (rich: Reassign, Comment, Status, Complete) */}
      {selectedTaskId && (
        <ActionDetailModal
          isOpen={isModalOpen}
          taskId={selectedTaskId}
          onClose={handleCloseModal}
          onUpdated={loadData}
        />
      )}

      {/* New Action Modal */}
      {showAddTaskModal && currentUser && (
        <AddTaskModal
          isOpen={showAddTaskModal}
          onClose={() => setShowAddTaskModal(false)}
          onSuccess={() => {
            setShowAddTaskModal(false);
            loadData();
          }}
          createdById={currentUser.id}
        />
      )}

      {/* Calendar task hover popup */}
      {hoveredTask && (() => {
        const { task, rect } = hoveredTask;
        const user = users.find(u => u.id === task.responsible_user_id);
        const badge = getStatusBadge(task);
        const riskLevel = getTaskPriority(task);
        const priorityClass = getPriorityClass(task);
        // Position: prefer right of the task, flip left if near right edge
        const popupWidth = 280;
        const spaceRight = window.innerWidth - rect.right;
        const left = spaceRight > popupWidth + 16 ? rect.right + 8 : rect.left - popupWidth - 8;
        const top = Math.min(rect.top, window.innerHeight - 240);
        return (
          <div
            className={`cal-task-popup ${priorityClass}`}
            style={{ position: 'fixed', left, top, width: popupWidth, zIndex: 9999 }}
            onMouseEnter={() => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }}
            onMouseLeave={() => setHoveredTask(null)}
          >
            <div className="cal-popup__header">
              <span className={`cal-popup__priority-bar ${priorityClass}`}></span>
              <p className="cal-popup__description">{task.description}</p>
            </div>
            <div className="cal-popup__meta">
              {task.facility_name && (
                <div className="cal-popup__row">
                  <Building2 size={12} />
                  <span>{task.facility_name}</span>
                </div>
              )}
              {task.client_name && (
                <div className="cal-popup__row">
                  <Package size={12} />
                  <span>{task.client_name}</span>
                </div>
              )}
              {user && (
                <div className="cal-popup__row">
                  <User size={12} />
                  <span>{user.name}</span>
                </div>
              )}
              {task.due_by && (
                <div className="cal-popup__row">
                  <Clock size={12} />
                  <span>Due {formatShortDate(task.due_by)}</span>
                </div>
              )}
            </div>
            <div className="cal-popup__footer">
              <span className={`cal-popup__status ${badge.class}`}>{badge.label}</span>
              <span className={`cal-popup__risk risk-${riskLevel}`}>{priorityLabel(riskLevel)}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ActionsInbox;
